import { useEffect, useRef, useState, useCallback } from 'react';
import { encodeJson, ATTR, HOST_IDENTITY, type JoinResponse, type DisplayInfo } from '@gonzovnc/protocol';
import { ConnectionQuality, Room, RoomEvent, Track, VideoQuality, type Participant as LkParticipant, type RemoteAudioTrack, type RemoteTrack, type RemoteVideoTrack, type RemoteTrackPublication, type RemoteParticipant as LkRemote } from 'livekit-client';
import { GonzoApi } from '../core/api.js';
import { Session } from '../core/session.js';
import type { RemoteParticipant } from '../core/types.js';
import { LiveKitTransport } from '../adapters/livekit-transport.js';
import { InputCapture } from '../adapters/input-capture.js';
import { readVideoStats } from '../adapters/video-stats-reader.js';
import { deriveStats, type RtpSnapshot, type VideoStats } from '../core/video-stats.js';

interface Props {
  api: GonzoApi;
  join: JoinResponse;
  onLeave: () => void;
}

interface ChatMsg { id: string; name: string; body: string; ts: number }

// On-screen keys that mobile soft keyboards lack (§6.11). Modifier `bit` matches the Host's
// CGEventFlags mapping (shift=1, ctrl=2, option=4, cmd=8).
const MOD_KEYS = [
  { code: 'ControlLeft', label: '⌃', title: 'Control', bit: 2 },
  { code: 'AltLeft', label: '⌥', title: 'Option/Alt', bit: 4 },
  { code: 'MetaLeft', label: '⌘', title: 'Command', bit: 8 },
  { code: 'ShiftLeft', label: '⇧', title: 'Shift', bit: 1 },
];
const SPECIAL_KEYS = [
  { code: 'Escape', key: 'Escape', label: 'esc' },
  { code: 'Tab', key: 'Tab', label: 'tab' },
  { code: 'ArrowLeft', key: 'ArrowLeft', label: '←' },
  { code: 'ArrowUp', key: 'ArrowUp', label: '↑' },
  { code: 'ArrowDown', key: 'ArrowDown', label: '↓' },
  { code: 'ArrowRight', key: 'ArrowRight', label: '→' },
  { code: 'Enter', key: 'Enter', label: '⏎' },
  { code: 'Backspace', key: 'Backspace', label: '⌫' },
  { code: 'Delete', key: 'Delete', label: 'del' },
  { code: 'Home', key: 'Home', label: 'home' },
  { code: 'End', key: 'End', label: 'end' },
  { code: 'PageUp', key: 'PageUp', label: 'pgup' },
  { code: 'PageDown', key: 'PageDown', label: 'pgdn' },
];
const FN_KEYS = Array.from({ length: 12 }, (_, i) => ({ code: `F${i + 1}`, key: `F${i + 1}`, label: `F${i + 1}` }));

// Touch-primary devices (phones/tablets) get the crosshair + pan/zoom input model instead of
// direct pointer mapping. Devices with a mouse (pointer: fine) keep the precise pointer path.
const IS_TOUCH = typeof window !== 'undefined'
  && (window.matchMedia?.('(pointer: coarse)').matches || (navigator.maxTouchPoints ?? 0) > 0);

// Element fullscreen is unavailable on iPhone Safari; hide the button there.
const FS_SUPPORTED = typeof document !== 'undefined' && !!document.documentElement.requestFullscreen;

// Subscriber-side quality rungs against the Host's simulcast layers: Sharp rides the full
// layer, Balanced/Smooth drop to lower spatial layers to cut bitrate (and lag) per viewer.
type VidQ = 'high' | 'medium' | 'low';
const QUALITY_MAP: Record<VidQ, VideoQuality> = {
  high: VideoQuality.HIGH,
  medium: VideoQuality.MEDIUM,
  low: VideoQuality.LOW,
};

export function RoomPage({ api, join, onLeave }: Props) {
  const roomRef = useRef<HTMLDivElement>(null); // fullscreen target (stage + dock + overlays)
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioBin = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<Session | null>(null);
  const captureRef = useRef<InputCapture | null>(null);
  const transportRef = useRef<LiveKitTransport | null>(null);
  const screenPubRef = useRef<RemoteTrackPublication | null>(null); // host screen track, for quality picks
  const screenTrackRef = useRef<RemoteTrack | null>(null);          // same track, for reading live stats
  const prevSnapRef = useRef<RtpSnapshot | null>(null);             // last stats sample (rates need two)

  const [roster, setRoster] = useState<RemoteParticipant[]>([]);
  const [chat, setChat] = useState<ChatMsg[]>([]);
  const [controller, setController] = useState<string | null>(join.role === 'controller' ? join.identity : null);
  const [privacyOn, setPrivacyOn] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [offered, setOffered] = useState(false);
  const [displays, setDisplays] = useState<DisplayInfo[]>([]);
  const [activeDisp, setActiveDisp] = useState('');
  // Voice-first (Zoom-like): the side dock stays hidden until you ask for People/Chat.
  const [dockOpen, setDockOpen] = useState(false);
  const [tab, setTab] = useState<'people' | 'chat' | 'manage'>('people');
  const [unread, setUnread] = useState(0); // chat messages received while the chat pane is hidden
  // Manage tab (admin): invite-code management. Plaintext codes are only returned at mint time
  // (the relay stores hashes), so a freshly minted code is shown once, prominently, with copy.
  const [codes, setCodes] = useState<{ codeId: string; role: string; expiresAt: string; uses: number; revoked: boolean }[]>([]);
  const [minted, setMinted] = useState<{ code: string; role: string } | null>(null);
  const [mintRole, setMintRole] = useState<'viewer' | 'controller'>('viewer');
  const [mintTtl, setMintTtl] = useState(86400);
  const [mintBusy, setMintBusy] = useState(false);
  const [micOn, setMicOn] = useState(false);
  const [audioInputs, setAudioInputs] = useState<MediaDeviceInfo[]>([]); // microphones for the picker
  const [micId, setMicId] = useState(''); // chosen input deviceId ('' = system default)
  const [speakerOn, setSpeakerOn] = useState(true); // room audio playback (host + voices)
  const [needsAudioStart, setNeedsAudioStart] = useState(false); // browser blocked autoplay
  const [handUp, setHandUp] = useState(false);
  const [hands, setHands] = useState<Set<string>>(new Set());       // identities with a raised hand
  const [speaking, setSpeaking] = useState<Set<string>>(new Set()); // identities currently talking
  const [typers, setTypers] = useState<Map<string, number>>(new Map()); // identity -> expiry ms
  const [vidQuality, setVidQuality] = useState<VidQ>('high');
  const [connected, setConnected] = useState(false);       // LiveKit is up: roster/publications known
  const [hostSharing, setHostSharing] = useState(false);   // presenter has a screen published
  const [videoPaused, setVideoPaused] = useState(false);   // SFU paused the layer (resuming shortly)
  const [stats, setStats] = useState<VideoStats | null>(null);
  const [statsOn, setStatsOn] = useState(false);           // "why is this blurry?" readout
  const [fs, setFs] = useState(false);           // we are the fullscreen element
  const [overlayShown, setOverlayShown] = useState(true); // fullscreen pill auto-hide
  const [banner, setBanner] = useState('');
  const [draft, setDraft] = useState('');
  const [keysOpen, setKeysOpen] = useState(false);
  const [fnOpen, setFnOpen] = useState(false);
  const [stickyMods, setStickyMods] = useState<Set<string>>(new Set());
  const [locked, setLocked] = useState(false); // desktop: mouse captured (pointer-locked) into the page
  const [quality, setQuality] = useState<ConnectionQuality>(ConnectionQuality.Unknown); // my link quality
  const [elapsed, setElapsed] = useState(0); // session seconds, shown in the bar
  const [copied, setCopied] = useState(false); // invite-link copy feedback
  const kbdSinkRef = useRef<HTMLTextAreaElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null); // predicted remote-cursor dot (desktop pointer-lock)

  // Event handlers fire from long-lived listeners; these refs mirror the current state for them.
  const handUpRef = useRef(false);
  const speakerOnRef = useRef(true);
  const vidQualityRef = useRef<VidQ>('high');
  const chatVisibleRef = useRef(false);
  const typingSentRef = useRef(0); // last time we broadcast typing 'start'

  // Session clock (people expect to see how long the hangout has been running).
  useEffect(() => {
    const t0 = Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, []);

  // Admin code management (Manage tab).
  const refreshCodes = useCallback(() => {
    void api.listCodes().then((r) => setCodes(r.codes)).catch(() => undefined);
  }, [api]);

  const mintCode = useCallback(async () => {
    setMintBusy(true);
    try {
      const c = await api.createCode(mintRole, mintTtl > 0 ? mintTtl : undefined);
      setMinted({ code: c.code, role: c.role });
      refreshCodes();
    } catch { setBanner('Could not create a code.'); }
    finally { setMintBusy(false); }
  }, [api, mintRole, mintTtl, refreshCodes]);

  // Paste the helper's clipboard into the Mac as typed text (rides the proven input path).
  const pasteToMac = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) captureRef.current?.sendText(text.slice(0, 4000));
    } catch { setBanner('Clipboard read was blocked — allow clipboard access and try again.'); }
  }, []);

  const copyInvite = useCallback(() => {
    let code = '';
    try { code = sessionStorage.getItem('gv.code') ?? ''; } catch { /* private mode */ }
    const text = code ? `${window.location.origin}/j/${code}` : window.location.origin;
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  const isAdmin = join.admin;
  const iAmController = controller === join.identity;

  const pushChat = useCallback((m: ChatMsg, mine = false) => {
    setChat((c) => [...c.slice(-199), m]);
    if (!mine && !chatVisibleRef.current) setUnread((u) => u + 1);
  }, []);

  const modBits = useCallback(
    () => [...stickyMods].reduce((m, c) => m | (MOD_KEYS.find((k) => k.code === c)?.bit ?? 0), 0),
    [stickyMods],
  );
  const toggleMod = useCallback((code: string) => setStickyMods((s) => {
    const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n;
  }), []);
  const tapSpecial = useCallback((code: string, key: string) => {
    captureRef.current?.tapKey(code, key, modBits());
    setStickyMods((s) => (s.size ? new Set() : s)); // sticky modifiers are one-shot
  }, [modBits]);

  // Route the mobile OS keyboard (summoned into the hidden sink) to the Host via native
  // beforeinput/compositionend (more reliable across iOS/Android than React's synthetic event).
  useEffect(() => {
    const sink = kbdSinkRef.current;
    if (!sink) return;
    const clear = () => { sink.value = ''; };
    const onBeforeInput = (ev: Event) => {
      const ie = ev as InputEvent;
      const cap = captureRef.current;
      if (!cap) return;
      const type = ie.inputType;
      if ((type === 'insertText' || type === 'insertFromPaste' || type === 'insertReplacementText') && ie.data) { cap.sendText(ie.data); ie.preventDefault(); }
      else if (type === 'insertLineBreak' || type === 'insertParagraph') { cap.tapKey('Enter', 'Enter', 0); ie.preventDefault(); }
      else if (type.startsWith('deleteContent')) { cap.tapKey('Backspace', 'Backspace', 0); ie.preventDefault(); }
    };
    const onCompositionEnd = (ev: CompositionEvent) => { if (ev.data) captureRef.current?.sendText(ev.data); clear(); };
    sink.addEventListener('beforeinput', onBeforeInput);
    sink.addEventListener('compositionend', onCompositionEnd);
    sink.addEventListener('input', clear); // safety: keep the sink empty
    return () => {
      sink.removeEventListener('beforeinput', onBeforeInput);
      sink.removeEventListener('compositionend', onCompositionEnd);
      sink.removeEventListener('input', clear);
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    const handTimers = new Set<number>(); // pending late-joiner hand re-announcements
    const unsubs: Array<() => void> = []; // transport subscriptions to release on teardown
    (async () => {
      const transport = await LiveKitTransport.connect(join.wsUrl, join.lkToken);
      if (disposed) { await transport.disconnect(); return; }
      transportRef.current = transport;
      const room = transport.room;

      const attachTrack = (track: RemoteTrack, pub: RemoteTrackPublication, participant: LkRemote) => {
        if (track.kind === Track.Kind.Video && participant.identity === HOST_IDENTITY) {
          if (videoRef.current) track.attach(videoRef.current);
          // This is a remote-control stage, not a movie: Chrome's default video jitter buffer is
          // pure added latency here. The SDK never sets this itself (it only ever clears it).
          try { (track as RemoteVideoTrack).setPlayoutDelay(0); } catch { /* not supported: fine */ }
          // Remember the screen publication so the viewer's quality picker can downshift
          // simulcast layers; re-apply the current pick if the share was restarted.
          screenPubRef.current = pub;
          screenTrackRef.current = track;
          setHostSharing(true);
          if (vidQualityRef.current !== 'high') pub.setVideoQuality(QUALITY_MAP[vidQualityRef.current]);
        } else if (track.kind === Track.Kind.Audio) {
          const el = track.attach();
          el.autoplay = true;
          // Speaker mute needs BOTH: track volume (survives the SDK's startAudio(), which
          // force-flips el.muted during autoplay recovery) AND el.muted (iOS Safari ignores
          // programmatic element volume, so volume alone is a no-op there).
          (track as RemoteAudioTrack).setVolume(speakerOnRef.current ? 1 : 0);
          el.muted = !speakerOnRef.current;
          audioBin.current?.appendChild(el);
        }
      };
      room.on(RoomEvent.TrackSubscribed, (t: RemoteTrack, pub: RemoteTrackPublication, p: LkRemote) => attachTrack(t, pub, p));
      room.on(RoomEvent.TrackUnsubscribed, (t: RemoteTrack, pub: RemoteTrackPublication) => {
        if (screenPubRef.current === pub) {
          // The presenter stopped sharing this screen (they may be about to share another one) —
          // they have NOT left. Say so, instead of leaving a frozen last frame on the stage.
          screenPubRef.current = null;
          screenTrackRef.current = null;
          setHostSharing(false);
          setVideoPaused(false); // stream state is per-track; the next share starts Active and
                                 // would never emit an event to clear a stale "Resuming…"
          setStats(null);
          prevSnapRef.current = null;
        }
        // Never .remove() the React-owned stage <video>: React won't re-insert it, and the
        // re-subscribe after a reconnect would attach to an orphaned node (permanent black stage).
        t.detach().forEach((e) => { if (e !== videoRef.current) e.remove(); });
      });
      room.on(RoomEvent.ConnectionQualityChanged, (q: ConnectionQuality, p: LkParticipant) => {
        if (p.identity === join.identity) setQuality(q);
      });
      room.on(RoomEvent.ActiveSpeakersChanged, (sp: LkParticipant[]) => {
        setSpeaking(new Set(sp.map((p) => p.identity)));
      });
      // The SFU pauses a simulcast layer nobody is watching and resumes it on demand (e.g. when you
      // switch Smooth -> Sharp, which also needs a fresh keyframe). Say "resuming" rather than
      // leaving a stale frame that reads as a freeze.
      room.on(RoomEvent.TrackStreamStateChanged, (pub: RemoteTrackPublication, state: Track.StreamState) => {
        if (pub === screenPubRef.current) setVideoPaused(state === Track.StreamState.Paused);
      });
      // Browsers may block audio autoplay until a gesture; surface a one-tap unblock.
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        setNeedsAudioStart(!room.canPlaybackAudio);
        // startAudio() force-unmutes every element as part of autoplay recovery — re-assert.
        audioBin.current?.querySelectorAll('audio').forEach((el) => { el.muted = !speakerOnRef.current; });
      });
      if (!room.canPlaybackAudio) setNeedsAudioStart(true);
      // attach already-subscribed tracks
      room.remoteParticipants.forEach((p) => p.trackPublications.forEach((pub) => { if (pub.track) attachTrack(pub.track, pub as RemoteTrackPublication, p); }));
      // Whether the host is sharing is known from their publications the moment we're connected —
      // don't flash "the presenter isn't sharing" at everyone while the first track subscribes.
      const hostPub = room.remoteParticipants.get(HOST_IDENTITY);
      if (hostPub && [...hostPub.trackPublications.values()].some((pub) => pub.kind === Track.Kind.Video)) {
        setHostSharing(true);
      }
      setConnected(true);

      const session = new Session(transport, {
        onRoster: setRoster,
        onChat: (m) => pushChat({ id: m.id, name: m.name, body: m.body, ts: m.ts }),
        onControl: (target) => { setController(target); if (target !== join.identity) setCapturing(false); },
        onPrivacy: (on) => setPrivacyOn(on),
        onEnding: (inSec, reason) => setBanner(`Room ending in ${inSec}s (${reason})`),
        onKicked: (target) => { if (target === join.identity) { setBanner('You were removed from the room.'); onLeave(); } },
        // `hello` is re-sent to heal a lost one, so this must be idempotent: clobbering activeDisp
        // would re-create the InputCapture (its effect keys on it) and drop pointer lock mid-drag.
        onCaptureEnabled: (d) => {
          setDisplays(d);
          setActiveDisp((cur) => (cur && d.some((x) => x.disp === cur) ? cur : (d[0]?.disp ?? '')));
          setCapturing(true);
        },
        onCaptureDisabled: () => setCapturing(false),
        onHand: (from, up) => setHands((prev) => {
          if (prev.has(from) === up) return prev;
          const n = new Set(prev); up ? n.add(from) : n.delete(from); return n;
        }),
        onTyping: (from, typing) => setTypers((prev) => {
          const n = new Map(prev);
          typing ? n.set(from, Date.now() + 5000) : n.delete(from);
          return n;
        }),
        onError: (code) => setBanner(`Error: ${code}`),
      });
      sessionRef.current = session;
      setRoster(transport.participants());

      // detect a control offer on our own attributes (ATTR.CTL === 'offered')
      unsubs.push(transport.onAttributesChanged((identity, attrs) => {
        if (identity === join.identity) setOffered(attrs[ATTR.CTL] === 'offered');
      }));

      // Drop leavers from the hand/typing sets so no ghosts linger. Late joiners can't see past
      // data messages, so re-announce my raised hand to each newcomer — delayed and repeated,
      // because their data channel may not be open the instant ParticipantConnected fires (a
      // send that early is silently lost).
      unsubs.push(transport.onParticipant((p, left) => {
        if (disposed) return;
        if (left) {
          setHands((prev) => { if (!prev.has(p.identity)) return prev; const n = new Set(prev); n.delete(p.identity); return n; });
          setTypers((prev) => { if (!prev.has(p.identity)) return prev; const n = new Map(prev); n.delete(p.identity); return n; });
          return;
        }
        if (!handUpRef.current) return;
        for (const delay of [1500, 4000]) {
          const t = window.setTimeout(() => {
            handTimers.delete(t);
            if (handUpRef.current) {
              transport.publishData('hand.v1', encodeJson({ v: 1, kind: 'hand', state: 'up', reason: 'speak' }), { reliable: true, destinationIdentities: [p.identity] });
            }
          }, delay);
          handTimers.add(t);
        }
      }));

      // Start the history fetch now — the mic permission prompt below must not delay chat.
      const histPromise = api.getChat();

      // Voice-first: arrive un-muted (like walking into a meeting), with a clear mute control.
      // Not awaited — a permission prompt can sit open indefinitely and must not block join.
      // A denied/blocked permission just leaves the mic off; the bar button retries.
      void room.localParticipant.setMicrophoneEnabled(true)
        .then(() => {
          if (disposed) return;
          setMicOn(true);
          setBanner((b) => (b.startsWith('Mic is blocked') ? '' : b));
          void refreshMics();
        })
        .catch(() => { if (!disposed) setBanner('Mic is blocked — use the 🔇 button after allowing microphone access.'); });

      // Load chat history for late joiners, merged by id with anything that arrived live during
      // the fetch — a message the unread badge counted must never be silently dropped.
      try {
        const hist = await histPromise;
        if (!disposed) {
          const mapped = hist.messages.map((m) => ({ id: m.id, name: m.name, body: m.body, ts: Date.parse(m.ts) }));
          const seen = new Set(mapped.map((m) => m.id));
          setChat((live) => [...mapped, ...live.filter((m) => !seen.has(m.id))].slice(-200));
        }
      } catch { /* non-fatal */ }
    })();

    return () => {
      disposed = true;
      unsubs.forEach((u) => u());
      handTimers.forEach((t) => clearTimeout(t));
      captureRef.current?.detach();
      sessionRef.current?.dispose();
      void transportRef.current?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // attach/detach input capture as control is armed/disarmed
  useEffect(() => {
    const session = sessionRef.current;
    if (!session || !stageRef.current) return;
    if (capturing) {
      const cap = new InputCapture(session);
      cap.setDisplay(activeDisp);
      cap.attach(stageRef.current, { touch: IS_TOUCH });
      cap.setKeyboardSink(kbdSinkRef.current);
      if (IS_TOUCH) {
        // Touch: fullscreen + crosshair. Fullscreen the ROOM root (not the stage) so the dock
        // (chat) and overlay pill stay inside the fullscreen subtree and remain usable.
        void cap.enterImmersive(roomRef.current ?? undefined);
      } else {
        cap.setSyntheticCursor(cursorRef.current); // desktop: pointer-lock capture/release
        cap.onLock(setLocked);
      }
      captureRef.current = cap;
      return () => { cap.detach(); setLocked(false); };
    }
    captureRef.current?.detach();
    captureRef.current = null;
    setKeysOpen(false);
    setLocked(false);
    setStickyMods(new Set());
    return undefined;
  }, [capturing, activeDisp]);

  // ---- chat + typing ---------------------------------------------------------------------

  const sendTypingState = useCallback((start: boolean) => {
    transportRef.current?.publishData('typing.v1', encodeJson({ v: 1, kind: 'typing', state: start ? 'start' : 'stop' }), { reliable: false });
  }, []);

  const onDraftChange = useCallback((v: string) => {
    setDraft(v);
    if (!v) {
      if (typingSentRef.current) { sendTypingState(false); typingSentRef.current = 0; }
      return;
    }
    const now = Date.now();
    if (now - typingSentRef.current > 2500) { typingSentRef.current = now; sendTypingState(true); }
  }, [sendTypingState]);

  const sendChat = useCallback(() => {
    const body = draft.trim();
    if (!body) return;
    const id = crypto.randomUUID();
    const ts = Date.now();
    sessionRef.current?.sendChat({ v: 1, t: 'msg', id, ts, body });
    void api.postChat(id, body).catch(() => undefined);
    pushChat({ id, name: 'You', body, ts }, true);
    setDraft('');
    if (typingSentRef.current) { sendTypingState(false); typingSentRef.current = 0; }
  }, [draft, api, pushChat, sendTypingState]);

  // Expire stale typers (a closed tab never sends 'stop').
  useEffect(() => {
    if (typers.size === 0) return;
    const iv = setInterval(() => {
      const now = Date.now();
      setTypers((prev) => {
        let changed = false;
        const n = new Map(prev);
        for (const [k, exp] of n) if (exp <= now) { n.delete(k); changed = true; }
        return changed ? n : prev;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [typers.size !== 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const chatVisible = dockOpen && tab === 'chat';
  useEffect(() => {
    chatVisibleRef.current = chatVisible;
    if (chatVisible) setUnread(0);
  }, [chatVisible]);

  // Keep the newest message in view whenever the chat pane is showing.
  useEffect(() => {
    if (chatVisible && chatBodyRef.current) chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
  }, [chat, chatVisible, typers]);

  const openDock = useCallback((t: 'people' | 'chat' | 'manage') => {
    if (t === 'manage') refreshCodes();
    if (dockOpen && tab === t) { setDockOpen(false); return; } // the active tab's button closes the dock
    setTab(t);
    setDockOpen(true);
  }, [dockOpen, tab, refreshCodes]);

  // ---- audio -------------------------------------------------------------------------------

  // Enumerate microphones for the picker. Device labels only populate once mic permission is
  // granted (i.e. after the first Mic-on), so we refresh again then and on any device change.
  const refreshMics = useCallback(async () => {
    try { setAudioInputs(await Room.getLocalDevices('audioinput')); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    void refreshMics();
    const md = navigator.mediaDevices;
    md?.addEventListener?.('devicechange', refreshMics);
    return () => md?.removeEventListener?.('devicechange', refreshMics);
  }, [refreshMics]);

  const toggleMic = useCallback(async () => {
    const room = transportRef.current?.room;
    if (!room) return;
    const next = !micOn;
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      if (next && micId) { try { await room.switchActiveDevice('audioinput', micId); } catch { /* device gone */ } }
      setMicOn(next);
      void refreshMics(); // labels are available now that permission was granted
    } catch { setBanner('Mic is blocked — allow microphone access in the browser and try again.'); }
  }, [micOn, micId, refreshMics]);

  // Pick a specific microphone; switches live if the mic is already on, else applies on next Mic-on.
  const changeMic = useCallback(async (id: string) => {
    setMicId(id);
    const room = transportRef.current?.room;
    if (room && micOn && id) { try { await room.switchActiveDevice('audioinput', id); } catch { /* ignore */ } }
  }, [micOn]);

  // Mute what I hear (host computer audio + everyone's voices) without leaving. Applies both
  // track volume (survives startAudio()'s el.muted rewrite) and el.muted (iOS ignores volume).
  const toggleSpeaker = useCallback(() => {
    setSpeakerOn((on) => {
      const next = !on;
      speakerOnRef.current = next;
      transportRef.current?.room.remoteParticipants.forEach((p) => {
        p.audioTrackPublications.forEach((pub) => {
          (pub.track as RemoteAudioTrack | undefined)?.setVolume(next ? 1 : 0);
        });
      });
      audioBin.current?.querySelectorAll('audio').forEach((el) => { el.muted = !next; });
      return next;
    });
  }, []);

  const startAudio = useCallback(() => {
    void transportRef.current?.room.startAudio()
      .then(() => setNeedsAudioStart(false))
      .catch(() => undefined);
  }, []);

  const toggleHand = useCallback(() => {
    const next = !handUp;
    transportRef.current?.publishData('hand.v1', encodeJson({ v: 1, kind: 'hand', state: next ? 'up' : 'down', reason: 'speak' }), { reliable: true });
    setHandUp(next);
    handUpRef.current = next;
    setHands((prev) => {
      const n = new Set(prev); next ? n.add(join.identity) : n.delete(join.identity); return n;
    });
  }, [handUp, join.identity]);

  // ---- video quality (per-viewer simulcast layer pick) ---------------------------------------

  const applyQuality = useCallback((q: VidQ) => {
    setVidQuality(q);
    vidQualityRef.current = q;
    screenPubRef.current?.setVideoQuality(QUALITY_MAP[q]);
  }, []);

  // Live quality readout. Turns "it looks blurry" into numbers: which simulcast rung is actually
  // arriving (a Sharp viewer stuck on 640x360 is being demoted upstream — a bandwidth problem),
  // the encoder's quantizer (the literal measure of blur), and jitter-buffer/freeze (lateness).
  useEffect(() => {
    if (!statsOn) { prevSnapRef.current = null; return; }
    let alive = true;
    const tick = async () => {
      const track = screenTrackRef.current;
      if (!track) { setStats(null); return; }
      const snap = await readVideoStats(track as RemoteVideoTrack);
      if (!alive || !snap) return;
      // The rungs are fractions of the host's capture, so name them relative to it.
      const captureWidth = screenPubRef.current?.dimensions?.width ?? 0;
      setStats(deriveStats(snap, prevSnapRef.current, captureWidth));
      prevSnapRef.current = snap;
    };
    void tick();
    const iv = setInterval(() => void tick(), 1000);
    return () => { alive = false; clearInterval(iv); };
  }, [statsOn, hostSharing]);

  // ---- fullscreen ----------------------------------------------------------------------------

  const toggleFs = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    else void roomRef.current?.requestFullscreen().catch(() => undefined);
  }, []);

  useEffect(() => {
    const onChange = () => setFs(document.fullscreenElement === roomRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Auto-hide the fullscreen overlay after a few idle seconds; any pointer motion re-shows it.
  useEffect(() => {
    if (!fs) { setOverlayShown(true); return; }
    // While touch-controlling, never auto-hide: the "reveal" tap would land on the stage and
    // double as a remote click. The pill stays up, like the keys bar.
    if (IS_TOUCH && capturing) { setOverlayShown(true); return; }
    const root = roomRef.current;
    if (!root) return;
    let timer = 0;
    const poke = () => {
      setOverlayShown(true);
      clearTimeout(timer);
      timer = window.setTimeout(() => setOverlayShown(false), 3000);
    };
    poke();
    root.addEventListener('pointermove', poke);
    root.addEventListener('touchstart', poke, { passive: true });
    return () => {
      clearTimeout(timer);
      root.removeEventListener('pointermove', poke);
      root.removeEventListener('touchstart', poke);
    };
  }, [fs, capturing]);

  const controllerName = controller ? roster.find((p) => p.identity === controller)?.name ?? controller : null;
  const nameOf = useCallback((identity: string) => {
    if (identity === join.identity) return 'You';
    return roster.find((p) => p.identity === identity)?.name ?? identity;
  }, [roster, join.identity]);

  const talkerNames = [...speaking].map(nameOf);
  const typerNames = [...typers.keys()].filter((id) => id !== join.identity).map(nameOf);
  // Raised hands float to the top of People in the order they were raised (a meeting queue) —
  // the hands Set preserves raise insertion order.
  const handRank = new Map([...hands].map((id, i) => [id, i] as const));
  const sortedRoster = [...roster].sort((a, b) => {
    const ra = handRank.get(a.identity);
    const rb = handRank.get(b.identity);
    if (ra === undefined && rb === undefined) return 0;
    if (ra === undefined) return 1;
    if (rb === undefined) return -1;
    return ra - rb;
  });

  const quickChatBadge = unread > 0 && <span className="ucount">{unread > 99 ? '99+' : unread}</span>;

  return (
    <div ref={roomRef} className={`room${dockOpen ? '' : ' dock-closed'}${fs ? ' fs' : ''}`}>
      <div ref={stageRef} className={`stage${capturing ? ' controlling' : ''}${capturing && !IS_TOUCH && !locked ? ' uncaptured' : ''}`}>
        <video ref={videoRef} autoPlay playsInline muted={false} />
        <div ref={audioBin} style={{ display: 'none' }} />
        {capturing && IS_TOUCH && <div className="crosshair" aria-hidden="true" />}
        {capturing && !IS_TOUCH && <div ref={cursorRef} className={`synth-cursor${locked ? '' : ' hidden'}`} aria-hidden="true" />}
        {capturing && !IS_TOUCH && !locked && (
          <div className="capture-prompt" aria-hidden="true">
            <span className="cp-title">🖱️ Click to control</span>
            <span className="cp-sub">You drive the remote cursor · press <kbd>Esc</kbd> to release</span>
          </div>
        )}
        {capturing && !IS_TOUCH && locked && <div className="lock-hint"><kbd>Esc</kbd> to release the mouse</div>}
        {controllerName && <div className="chip"><span className="dot" />{controllerName} has control</div>}
        {talkerNames.length > 0 && (
          <div className="chip talkers" title="Speaking now"><span className="voice" aria-hidden="true" />{talkerNames.join(', ')}</div>
        )}
        {offered && !capturing && (
          <div className="offer-toast">
            <span>🖱️ You've been offered control of the shared Mac</span>
            <button className="primary" onClick={() => void api.acceptControl().then(() => setOffered(false))}>Accept control</button>
          </div>
        )}
        {needsAudioStart && speakerOn && (
          <button className="audio-unlock" onClick={startAudio}>🔊 Tap to enable sound</button>
        )}
        {privacyOn && <div className="placard">🔒 The presenter enabled Private Control.<br />The screen is paused for viewers.</div>}
        {/* Stopping the share is not hanging up — say so, instead of showing a frozen last frame. */}
        {!privacyOn && connected && !hostSharing && (
          <div className="placard">
            🖥️ The presenter isn't sharing a screen right now.<br />
            <span className="sub">You're still in the room — the screen will come back when they share again.</span>
          </div>
        )}
        {hostSharing && videoPaused && <div className="lock-hint">Resuming video…</div>}
        {statsOn && (
          <div className="statsbox" onPointerDown={(e) => e.stopPropagation()}>
            <div className="stats-head">
              <span>Video stats</span>
              <button className="small" onClick={() => setStatsOn(false)}>✕</button>
            </div>
            {stats ? (
              <table>
                <tbody>
                  <tr><td>Receiving</td><td>{stats.width}×{stats.height} <em>({stats.layer})</em></td></tr>
                  <tr><td>Frame rate</td><td>{stats.fps} fps</td></tr>
                  <tr><td>Bitrate</td><td>{stats.kbps} kbps</td></tr>
                  <tr title="Encoder quantizer: the numeric measure of blur. Lower is sharper; above ~35 text smears.">
                    <td>Sharpness (QP)</td><td>{stats.meanQp ?? '—'}</td>
                  </tr>
                  <tr><td>Buffer delay</td><td>{stats.jitterMs ?? '—'} ms</td></tr>
                  <tr><td>Round trip</td><td>{stats.rttMs ?? '—'} ms</td></tr>
                  <tr><td>Packet loss</td><td>{stats.lossPct}%</td></tr>
                  <tr><td>Freezes</td><td>{stats.freezes}</td></tr>
                  <tr><td>Codec</td><td>{stats.codec}</td></tr>
                </tbody>
              </table>
            ) : <div className="code-meta">Waiting for video…</div>}
          </div>
        )}
        {banner && <div className="banner" role="status" title="Click to dismiss" onClick={() => setBanner('')}>{banner}</div>}

        {/* Fullscreen control pill: the essentials (above all, mute) stay reachable without
            leaving fullscreen. Auto-hides when idle; taps must never leak into remote control. */}
        {fs && !locked && (
          <div
            className={`stage-overlay${overlayShown ? '' : ' hidden'}${capturing && keysOpen ? ' raised' : ''}`}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            <button className={`ov${micOn ? '' : ' alert'}`} title={micOn ? 'Mute microphone' : 'Unmute microphone'} onClick={() => void toggleMic()}>
              {micOn ? '🎙' : '🔇'}<span>{micOn ? 'Mute' : 'Unmute'}</span>
            </button>
            <button className={`ov${speakerOn ? '' : ' alert'}`} title={speakerOn ? 'Mute room audio' : 'Unmute room audio'} onClick={toggleSpeaker}>
              {speakerOn ? '🔊' : '🔈'}<span>Sound</span>
            </button>
            <button className={`ov${handUp ? ' on' : ''}`} title={handUp ? 'Lower hand' : 'Raise hand'} onClick={toggleHand}>
              ✋<span>{handUp ? 'Lower' : 'Hand'}</span>
            </button>
            <button className="ov" title="Chat" onClick={() => openDock('chat')}>
              💬{quickChatBadge}<span>Chat</span>
            </button>
            {/* Controllers keep their typing tools in fullscreen — the bar (their usual home) is hidden. */}
            {capturing && <button className="ov" title="Type your clipboard into the Mac" onClick={() => void pasteToMac()}>📋<span>Paste</span></button>}
            {capturing && IS_TOUCH && <button className="ov" title="On-screen keyboard" onClick={() => kbdSinkRef.current?.focus()}>⌨<span>Type</span></button>}
            {capturing && <button className={`ov${keysOpen ? ' on' : ''}`} title="Special keys (Esc, ⌘, arrows, F-keys…)" onClick={() => setKeysOpen((v) => !v)}>⌥<span>Keys</span></button>}
            <select className="qualpick" value={vidQuality} title="Video quality" onChange={(e) => applyQuality(e.target.value as VidQ)}>
              <option value="high">Sharp</option>
              <option value="medium">Balanced</option>
              <option value="low">Smooth</option>
            </select>
            <button className="ov" title="Exit fullscreen" onClick={toggleFs}>⛶<span>Exit</span></button>
          </div>
        )}

        {capturing && keysOpen && (
          <div className="keysbar" onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
            {MOD_KEYS.map((m) => (
              <button key={m.code} title={m.title} className={`kb mod${stickyMods.has(m.code) ? ' on' : ''}`}
                onClick={() => toggleMod(m.code)}>{m.label}</button>
            ))}
            {(fnOpen ? FN_KEYS : SPECIAL_KEYS).map((k) => (
              <button key={k.code} className="kb" onClick={() => tapSpecial(k.code, k.key)}>{k.label}</button>
            ))}
            <button className={`kb${fnOpen ? ' on' : ''}`} onClick={() => setFnOpen((v) => !v)}>Fn</button>
          </div>
        )}

        {/* Hidden sink: focusing it summons the mobile OS keyboard; input is routed to the Host. */}
        <textarea
          ref={kbdSinkRef}
          className="kbd-sink"
          tabIndex={-1}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          onPointerDown={(e) => e.stopPropagation()}
        />
      </div>

      <div className="dock">
        <div className="tabs">
          <button className={tab === 'people' ? 'on' : ''} onClick={() => setTab('people')}>People ({roster.length})</button>
          <button className={tab === 'chat' ? 'on' : ''} onClick={() => setTab('chat')}>Chat{tab !== 'chat' && quickChatBadge}</button>
          {isAdmin && <button className={tab === 'manage' ? 'on' : ''} onClick={() => { setTab('manage'); refreshCodes(); }}>Manage</button>}
          <button className="dock-close" title="Hide panel" onClick={() => setDockOpen(false)}>✕</button>
        </div>
        {tab === 'manage' && isAdmin ? (
          <div className="body manage">
            <h4>Invite codes</h4>
            <div className="mint-row">
              <select value={mintRole} onChange={(e) => setMintRole(e.target.value as 'viewer' | 'controller')}>
                <option value="viewer">Viewer</option>
                <option value="controller">Controller</option>
              </select>
              <select value={mintTtl} onChange={(e) => setMintTtl(Number(e.target.value))}>
                <option value={3600}>1 hour</option>
                <option value={43200}>12 hours</option>
                <option value={86400}>24 hours</option>
                <option value={604800}>7 days</option>
              </select>
              <button className="small primary" disabled={mintBusy} onClick={() => void mintCode()}>New code</button>
            </div>
            {minted && (
              <div className="minted">
                <div className="minted-code">{minted.code}</div>
                <div className="minted-actions">
                  <button className="small" onClick={() => void navigator.clipboard?.writeText(minted.code)}>Copy code</button>
                  <button className="small" onClick={() => void navigator.clipboard?.writeText(`${window.location.origin}/j/${minted.code}`)}>Copy link</button>
                  <button className="small" onClick={() => setMinted(null)}>✕</button>
                </div>
                <div className="minted-note">Save it now — codes are stored hashed and can't be shown again.</div>
              </div>
            )}
            {codes.filter((c) => !c.revoked).map((c) => (
              <div className="code-row" key={c.codeId}>
                <span className={`badge ${c.role === 'controller' ? 'control' : 'admin'}`}>{c.role.toUpperCase()}</span>
                <span className="code-meta">{c.uses} use{c.uses === 1 ? '' : 's'} · expires {new Date(c.expiresAt).toLocaleString()}</span>
                <button className="small danger" title="Revoke this code" onClick={() => void api.revokeCode(c.codeId).then(refreshCodes)}>Revoke</button>
              </div>
            ))}
            {codes.filter((c) => !c.revoked).length === 0 && <div className="code-meta">No active codes.</div>}
          </div>
        ) : tab === 'people' ? (
          <div className="body">
            {sortedRoster.map((p) => (
              <div className={`roster-row${speaking.has(p.identity) ? ' talking' : ''}`} key={p.identity}>
                {hands.has(p.identity) && <span className="hand" title="Hand raised">✋</span>}
                <span className="name">{p.identity === join.identity ? `${p.name} (you)` : p.name}</span>
                {speaking.has(p.identity) && <span className="voice" title="Speaking" aria-label="speaking" />}
                {typers.has(p.identity) && p.identity !== join.identity && <span className="typing-dot" title="Typing…">✏️</span>}
                {p.attributes[ATTR.ADMIN] === 'true' && <span className="badge admin">ADMIN</span>}
                {p.identity === controller && <span className="badge control">CONTROL</span>}
                {isAdmin && p.identity !== join.identity && p.identity !== HOST_IDENTITY && (
                  <>
                    <button className="small" title="Grant control" onClick={() => void api.grantControl(p.identity)}>⌘</button>
                    <button className="small" title="Mute" onClick={() => void api.mute(p.identity)}>🔇</button>
                    <button className="small danger" title="Remove" onClick={() => void api.kick(p.identity)}>✕</button>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="body" ref={chatBodyRef}>
              {chat.map((m) => (
                <div className="chat-msg" key={m.id}><span className="who">{m.name}</span><div>{m.body}</div></div>
              ))}
              {typerNames.length > 0 && (
                <div className="typing-line">
                  {typerNames.length === 1 ? `${typerNames[0]} is typing…`
                    : typerNames.length === 2 ? `${typerNames[0]} and ${typerNames[1]} are typing…`
                    : 'Several people are typing…'}
                </div>
              )}
            </div>
            <div className="chat-input">
              <input value={draft} onChange={(e) => onDraftChange(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendChat(); }} placeholder="Message…" maxLength={2000} />
              <button className="small primary" onClick={sendChat}>Send</button>
            </div>
          </>
        )}
      </div>

      <div className="bar">
        <button className={micOn ? '' : 'alert'} title={micOn ? 'Mute microphone' : 'Unmute microphone'} onClick={() => void toggleMic()}>{micOn ? '🎙 Mute' : '🔇 Unmute'}</button>
        {audioInputs.length > 1 && (
          <select className="micpick" value={micId} title="Choose microphone" onChange={(e) => void changeMic(e.target.value)}>
            <option value="">Default mic</option>
            {audioInputs.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label || `Microphone ${d.deviceId.slice(0, 4)}`}</option>)}
          </select>
        )}
        <button className={speakerOn ? '' : 'alert'} title={speakerOn ? 'Mute room audio' : 'Unmute room audio'} onClick={toggleSpeaker}>{speakerOn ? '🔊' : '🔈'}</button>
        <button className={handUp ? 'primary' : ''} onClick={toggleHand}>✋ {handUp ? 'Lower' : 'Raise'} hand</button>
        <button className={dockOpen && tab === 'people' ? 'primary' : ''} title="People" onClick={() => openDock('people')}>👥 {roster.length}</button>
        <button className={dockOpen && tab === 'chat' ? 'primary' : ''} title="Chat" onClick={() => openDock('chat')}>💬{quickChatBadge}</button>
        {offered && <button className="primary" onClick={() => void api.acceptControl().then(() => setOffered(false))}>Accept control</button>}
        {isAdmin && !iAmController && <button onClick={() => void api.grantControl(join.identity)}>Take control</button>}
        {iAmController && <button onClick={() => void api.releaseControl()}>Release control</button>}
        {capturing && <button title="Type your clipboard into the Mac" onClick={() => void pasteToMac()}>📋 Paste</button>}
        {capturing && <button title="Show the on-screen keyboard" onClick={() => kbdSinkRef.current?.focus()}>⌨</button>}
        {capturing && <button className={keysOpen ? 'primary' : ''} title="Special keys (Esc, ⌘, arrows, F-keys…)" onClick={() => setKeysOpen((v) => !v)}>Keys</button>}
        {isAdmin && <button className={privacyOn ? 'primary' : ''} onClick={() => void api.setPrivacy(!privacyOn)}>{privacyOn ? '🔒 Private' : '🔓 Private control'}</button>}
        {displays.length > 1 && (
          <select value={activeDisp} onChange={(e) => setActiveDisp(e.target.value)}>
            {displays.map((d) => <option key={d.disp} value={d.disp}>{`Display ${d.disp.slice(0, 4)}`}</option>)}
          </select>
        )}
        <select className="qualpick" value={vidQuality} title="Video quality — lower it if the stream lags" onChange={(e) => applyQuality(e.target.value as VidQ)}>
          <option value="high">Sharp</option>
          <option value="medium">Balanced</option>
          <option value="low">Smooth</option>
        </select>
        <button className={statsOn ? 'primary' : ''} title="Video stats — what resolution/bitrate you're actually receiving" onClick={() => setStatsOn((v) => !v)}>📊</button>
        {FS_SUPPORTED && <button title={fs ? 'Exit fullscreen' : 'Fullscreen'} onClick={toggleFs}>⛶</button>}
        <span className="spacer" />
        <span className={`quality q-${quality}`} title={`Connection: ${quality}`}>
          <span className="qbar b1" /><span className="qbar b2" /><span className="qbar b3" />
        </span>
        <span className="clock" title="Session duration">{`${Math.floor(elapsed / 3600) ? `${Math.floor(elapsed / 3600)}:` : ''}${String(Math.floor((elapsed % 3600) / 60)).padStart(2, '0')}:${String(elapsed % 60).padStart(2, '0')}`}</span>
        <button title="Copy an invite link with the room code" onClick={copyInvite}>{copied ? '✓ Copied' : '🔗 Invite'}</button>
        {isAdmin && <button className="danger" onClick={() => void api.endRoom().then(onLeave)}>End room</button>}
        <button onClick={() => { void transportRef.current?.disconnect(); onLeave(); }}>Leave</button>
      </div>
    </div>
  );
}
